import type { FC, ReactElement, ReactNode } from "react"

import { LogoImage } from "@/3514/assets"
import { ParticipantFooter, ViewAsBanner } from "@/3514/components"
import {
    type TOrientation,
    useDefaultStylesReset,
    useHtmlScrollbarGutter,
    useIsFakeParticipant,
    useOrientation
} from "@/3514/hooks"
import { buildCssClassName } from "@/3514/utils"

type _TProps = { children: ReactNode }

const classes = {
    container: ({ isLandscape }: { isLandscape: boolean }): string =>
        buildCssClassName(
            "flex flex-col items-center [max-height:100dvh] h-full",
            isLandscape ? "pb-[20px] md:pb-[50px]" : "pb-[50px]"
        ),
    header: ({ isFakeParticipant }: { isFakeParticipant: boolean }): string =>
        buildCssClassName("px-[30px] py-[15px] w-full", isFakeParticipant && "max-md:mt-[70px]"),
    main: ({ isLandscape }: { isLandscape: boolean }): string =>
        buildCssClassName("md:pt-[140px] w-full flex justify-center relative", isLandscape || "pt-[100px]")
} as const

const CONTAINER_CLASS_NAME: string = "c-al"

const AuthLayout: FC<_TProps> = ({ children }: _TProps): ReactElement => {
    useHtmlScrollbarGutter({ isEnabled: true })

    useDefaultStylesReset(CONTAINER_CLASS_NAME)

    const { isLandscape }: TOrientation = useOrientation()

    const isFakeParticipant: boolean = useIsFakeParticipant()

    return (
        <div className={buildCssClassName(classes.container({ isLandscape }), CONTAINER_CLASS_NAME)}>
            {isFakeParticipant && <ViewAsBanner />}

            <header className={classes.header({ isFakeParticipant })}>
                <LogoImage />
            </header>

            <main className={classes.main({ isLandscape })}>{children}</main>

            <ParticipantFooter mode="auth" />
        </div>
    )
}

AuthLayout.displayName = "AuthLayout"

export { AuthLayout }
